/* eslint-disable no-throw-literal,no-unused-expressions */
/* global G */
import routeComposition from 'trait/composition/route'
import { getFirstItem } from 'lib/util'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'
import { setData } from 'lib/sequence/model/api/set'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import { actionWithHooks } from 'lib/sequence/module/action'
import refresh from 'lib/sequence/model/api/refresh'
import reset from 'lib/sequence/model/api/reset'
import infiniteWithDrafts from 'app/_shared/events/search/infiniteWithDrafts'

const NoInternetError = () => ({
  messageKey: 'label.noInternetConnection',
  message: 'No internet connection',
  ns: 'common',
})

const RejectionError = response => ({
  messageKey: 'text.operationCancelled',
  message: 'Operation cancelled',
  ...response,
})

/**
 * @typedef {Object} OptionPasswordResponse
 * @description A response returned by the option management endpoint.
 * @property {string} [password]          the option activation password
 * @property {string} [messageKey]        the translation key for a final message
 * @property {string} [message]           the default final message
 * @property {string} [dialogMessageKey]  the translation key for a message to be displayed if the
 *                                        generate request has to be confirmed
 * @property {string} [dialogMessage]     the default confirmation message
 * @property {string} [rejectMessageKey]  the translation key for a message to be displayed if the
 *                                        user cancels the confirmation dialog
 * @property {string} [rejectMessage]     the default reject message
 * @property {string} [confirmationNonce]
 */

/**
 * Shows a dialog asking the user for confirmation.
 *
 * @param {Gaia.AppModule.Spec} module    current module
 * @param {Object} info                   translation object
 * @param {string} info.dialogMessageKey  dialog message translation key
 * @param {string} info.dialogMessage     dialog message translation default text
 * @returns {Promise<*>}
 */
const showConfirmationDialog = async (module, info) => await showBlockingDialog(module, null, {
  title: {
    ns: 'optionManagement',
    key: `${info.dialogMessageKey}.title`,
    defaultValue: 'Confirmation',
  },
  text: {
    ns: 'optionManagement',
    key: `${info.dialogMessageKey}.text`,
    defaultValue: info.dialogMessage,
    md: true,
  },
  ok: {
    ns: 'common',
    key: 'button.confirm',
    defaultValue: 'Confirm',
  },
  cancel: {
    ns: 'common',
    key: 'button.cancel',
    defaultValue: 'Cancel',
  },
})

/**
 * Shows a dialog after the user has rejected a request required to finish the process.
 *
 * @param {Gaia.AppModule.Spec} module    current module
 * @param {Object} info                   translation object
 * @param {string} info.rejectMessageKey  dialog message translation key
 * @param {string} info.rejectMessage     dialog message translation default text
 * @returns {Promise<*>}
 */
const showRejectionDialog = async (module, info) => await showBlockingDialog(module, null, {
  title: {
    ns: 'optionManagement',
    key: `${info.rejectMessageKey}.title`,
    defaultValue: 'Option couldn\'t be activated',
  },
  text: {
    ns: 'optionManagement',
    key: `${info.rejectMessageKey}.text`,
    defaultValue: info.rejectMessage,
    md: true,
  },
  ok: {
    ns: 'common',
    key: 'button.ok',
    defaultValue: 'Ok',
  },
  cancel: null,
})

/**
 * Checks whether there is already a PIN for the current equipment.
 *
 * It uses the same handler (`infiniteWithDrafts`), with the same props as the list
 * in the `index` action. By doing that, we gain access to the same cached results
 * when offline through the persistence layer.
 * If we don't do this, we don't have access to the cached pins when being offline.
 *
 * NOTE: See dummy generation config in module config for props.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*): function(*, {pin: *, serial: *}): Promise<unknown>}
 */
const checkExistingPin = module => component => async (equipment, { pin, serial }) => {
  try {
    const existingDrafts = await infiniteWithDrafts('verbose', module, component, {})
    const pinDrafts = existingDrafts.filter(x => x.value?.type === 'pin')

    return pinDrafts.find(x => x.value.password === pin && x.value.serialNumber === serial)
  } catch (e) {
    console.error(e)
  }

  return []
}

/**
 * Attempts to obtain the password for an OptionManagement by sending a request with the `payload`
 * contents. If no `payload` is passed, its value is taken from the `request` attribute of the
 * module's (optionManagement/scan) model.
 *
 * If the response contains a `confirmationNonce` property, it uses the response messages to display
 * confirmation and rejection dialogs and performs the confirmation request accordingly.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @param {Object} [payload]            scanned code contents. if not passed, are taken from model
 * @returns {Promise<{response: OptionPasswordResponse, password?: string}>}
 */
export const requestOptionPassword = async (module, payload) => {
  const model = module[G.MODEL]
  const { version } = model[G.PROPS]
  const { request } = model[G.CHILDREN]
  const params = payload || request[G.CACHE]
  const online = module[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]

  if (!online) {
    throw NoInternetError()
  }

  let result

  const url = `/api/v${version}/optionmanagement`

  const options = { middleware: ({ persistence, ...middleware }) => Object.values(middleware) }
  /** @type {OptionPasswordResponse} */
  const response = await model[G.ADAPTER][G.HTTP].post({ url, params }, options)
  const { password, confirmationNonce } = response || {}

  result = { response }

  if (password) {
    result = { response, password }
  } else if (confirmationNonce) {
    if (await showConfirmationDialog(module, response)) {
      const confirmation = { ...params, confirmationNonce }
      result = await requestOptionPassword(module, confirmation)
    } else {
      await showRejectionDialog(module, response)
      throw RejectionError(response)
    }
  } else {
    throw response
  }
  return result
}

/**
 * Processes the option
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {(function({request: *}): Promise<*|undefined>)|*}
 */
const processOption = module => async (request) => {
  const intl = module[G.ADAPTER][G.INTL]
  const moduleState = module[G.STATE]
  const model = module[G.MODEL]
  const submitDate = new Date().toISOString()

  setData(model, {
    request,
    type: 'option',
    submitDate,
  }, {
    updateCache: true,
  })

  try {
    const result = await requestOptionPassword(module, request)

    const { response, password } = result
    setData(model, { response, password }, { updateCache: true })

    moduleState[G.ROUTE] = routeComposition(null, 'show')
    return await redirectSequence(module)({})
  } catch (e) {
    // const response = { messageKey: e.messageKey, message: e.message }
    setData(model, { response: e }, { updateCache: true })

    moduleState[G.ROUTE] = routeComposition(null, 'error')
    const error = await intl._t(e.messageKey, {
      _key: e.messageKey,
      ns: e.ns || 'optionManagement',
      defaultValue: e.message,
    })
    return await redirectSequence(module)({ error })
  }
}

/**
 * Processes the PIN
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*): function(*, *, *): Promise<*>}
 */
const processPin = module => component => async (request, response, data) => {
  const moduleState = module[G.STATE]
  const model = module[G.MODEL]
  const submitDate = new Date().toISOString()

  const { equipment, pin, software, serial } = data
  const productName = getFirstItem(equipment?.refs?.product)?.value?.name

  const existingPin = await checkExistingPin(module)(component)(equipment, { pin, serial })

  await refresh(model, {
    ...existingPin || {},
    type: 'pin',
    password: pin,
    softwareVersion: software,
    serialNumber: serial,
    deviceInformation: productName,
    request,
    response,
    submitDate,
  })

  if (equipment?.key !== -1) {
    await refresh(model[G.CHILDREN].equipment, equipment)
  } else {
    await reset(model[G.CHILDREN].equipment, { clear: true })
  }

  moduleState[G.ROUTE] = routeComposition(null, 'show')

  return await actionWithHooks(module[G.ACTIONS].submitPin)()
}

/**
 * OptionManagement generate action
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*): function(...[*]): Promise<*|undefined>}
 */
export default module => component => async (...args) => {
  const { payload: { request = {}, data = {}, response = {} } = {}, type = 'option' } = args[0] || {}

  return type === 'option'
    ? await processOption(module)(request)
    : await processPin(module)(component)(request, response, data)
}
